import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Clock,
  ChevronLeft,
  ChevronRight,
  Flag,
  Send,
  AlertTriangle,
  CheckCircle,
  Loader2,
  AlertCircle,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple Anti-cheat for public exams
const PublicAntiCheat = ({ attemptId, onViolation, enabled = true }) => {
  const violationCount = useRef(0);
  
  useEffect(() => {
    if (!enabled) return;
    
    const logViolation = async (type, details = '') => {
      violationCount.current += 1;
      
      try {
        await fetch(`${API_URL}/api/exams/proctoring/public/log`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attempt_id: attemptId,
            violation_type: type,
            details,
          }),
        });
        onViolation(violationCount.current);
      } catch (error) {
        console.error('Failed to log violation:', error);
      }
    };
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        logViolation('tab_switch', 'User switched tabs');
        toast.warning('Avertissement: Vous avez quitté l\'onglet de l\'examen');
      }
    };
    
    const handleBlur = () => {
      logViolation('window_blur', 'Window lost focus');
    };
    
    const handleContextMenu = (e) => {
      e.preventDefault();
      logViolation('right_click', 'User tried to right-click');
      toast.warning('Le clic droit est désactivé pendant l\'examen');
    };
    
    const handleKeyDown = (e) => {
      if (
        (e.ctrlKey && (e.key === 'c' || e.key === 'v' || e.key === 'p')) ||
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C'))
      ) {
        e.preventDefault();
        logViolation('keyboard_shortcut', `Blocked: ${e.key}`);
        toast.warning('Cette action est désactivée pendant l\'examen');
      }
    };
    
    const handleCopy = (e) => {
      e.preventDefault();
      logViolation('copy_attempt', 'User tried to copy');
      toast.warning('Le copier-coller est désactivé');
    };
    
    const handlePaste = (e) => {
      e.preventDefault();
      logViolation('paste_attempt', 'User tried to paste');
      toast.warning('Le copier-coller est désactivé');
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
    };
  }, [attemptId, enabled, onViolation]);
  
  return null;
};

// Timer component
const Timer = ({ timeRemaining, onTimeUp }) => {
  const [time, setTime] = useState(timeRemaining);
  
  useEffect(() => {
    if (time <= 0) {
      onTimeUp();
      return;
    }
    
    const timer = setInterval(() => {
      setTime(prev => {
        if (prev <= 1) {
          onTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [time, onTimeUp]);
  
  const minutes = Math.floor(time / 60);
  const seconds = time % 60;
  
  const isCriticalTime = time < 120; // Less than 2 minutes (blinks red)
  const isLowTime = time < 300; // Less than 5 minutes
  
  return (
    <div className={cn(
      "flex items-center gap-2 px-4 py-2 rounded-xl font-mono text-lg font-bold transition-all duration-500 border backdrop-blur-md",
      isCriticalTime 
        ? "bg-red-600/90 text-white border-red-500 animate-[pulse_1s_ease-in-out_infinite] shadow-[0_0_15px_rgba(239,68,68,0.8)]" 
        : isLowTime 
          ? "bg-amber-100/90 text-amber-800 border-amber-300 shadow-sm" 
          : "bg-white/80 text-slate-700 border-slate-200 shadow-sm"
    )}>
      <Clock className={cn("h-5 w-5", isCriticalTime && "animate-pulse")} />
      <span>
        {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
      </span>
    </div>
  );
};

export default function PublicExamTakePage() {
  const { slug, attemptId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [markedForReview, setMarkedForReview] = useState(new Set());
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [violations, setViolations] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const WARNING_THRESHOLD = 3;
  
  const autoSaveRef = useRef(null);
  const answersRef = useRef({});
  const submittingRef = useRef(false);
  
  // localStorage helper keys
  const LS_ANSWERS_KEY = `exam_answers_${attemptId}`;
  const LS_INDEX_KEY = `exam_index_${attemptId}`;
  const LS_REVIEW_KEY = `exam_review_${attemptId}`;
  
  // Keep answersRef in sync
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);
  
  // Save to localStorage on every answer change
  useEffect(() => {
    if (!attemptId || submitted) return;
    try {
      localStorage.setItem(LS_ANSWERS_KEY, JSON.stringify(answers));
    } catch (e) { /* quota exceeded, ignore */ }
  }, [answers, attemptId, submitted]);
  
  // Save currentIndex to localStorage
  useEffect(() => {
    if (!attemptId || submitted) return;
    try {
      localStorage.setItem(LS_INDEX_KEY, String(currentIndex));
    } catch (e) {}
  }, [currentIndex, attemptId, submitted]);
  
  // Save markedForReview to localStorage
  useEffect(() => {
    if (!attemptId || submitted) return;
    try {
      localStorage.setItem(LS_REVIEW_KEY, JSON.stringify(Array.from(markedForReview)));
    } catch (e) {}
  }, [markedForReview, attemptId, submitted]);
  
  // Warn before page unload
  useEffect(() => {
    if (submitted) return;
    const handleBeforeUnload = (e) => {
      if (Object.keys(answersRef.current).length > 0 && !submittingRef.current) {
        e.preventDefault();
        e.returnValue = 'Vos réponses non soumises seront sauvegardées. Voulez-vous vraiment quitter ?';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [submitted]);
  
  // Cleanup localStorage helper
  const clearLocalStorage = () => {
    try {
      localStorage.removeItem(LS_ANSWERS_KEY);
      localStorage.removeItem(LS_INDEX_KEY);
      localStorage.removeItem(LS_REVIEW_KEY);
    } catch (e) {}
  };

  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        }
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
      toast.error("Impossible d'activer le plein écran, votre navigateur ne le permet pas");
    }
  }, []);

  useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  useEffect(() => {
    startExam();
  }, [attemptId]);

  const startExam = async () => {
    try {
      // Start or resume the exam attempt
      const res = await fetch(`${API_URL}/api/exams/public/attempt/${attemptId}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        const errorMsg = errorData.detail || 'Impossible de démarrer l\'examen';
        
        // If time expired and auto-submitted, redirect to results
        if (errorMsg.includes('temps imparti') || errorMsg.includes('soumis automatiquement')) {
          clearLocalStorage();
          toast.info(errorMsg);
          navigate(`/exam/${slug}/results/${attemptId}`);
          return;
        }
        
        throw new Error(errorMsg);
      }
      
      const data = await res.json();
      setAttempt(data.attempt);
      setQuestions(data.questions);
      setTimeRemaining(data.time_remaining);
      
      // Merge answers: localStorage takes priority over server (more recent)
      const serverAnswers = data.attempt.answers || {};
      let mergedAnswers = { ...serverAnswers };
      try {
        const savedAnswers = localStorage.getItem(LS_ANSWERS_KEY);
        if (savedAnswers) {
          const parsed = JSON.parse(savedAnswers);
          // LocalStorage answers override server answers (they're more recent)
          mergedAnswers = { ...serverAnswers, ...parsed };
        }
      } catch (e) {}
      setAnswers(mergedAnswers);
      
      // Restore currentIndex from localStorage
      try {
        const savedIndex = localStorage.getItem(LS_INDEX_KEY);
        if (savedIndex !== null) {
          const idx = parseInt(savedIndex, 10);
          if (!isNaN(idx) && idx >= 0 && idx < data.questions.length) {
            setCurrentIndex(idx);
          }
        }
      } catch (e) {}
      
      // Restore markedForReview from localStorage or server
      try {
        const savedReview = localStorage.getItem(LS_REVIEW_KEY);
        if (savedReview) {
          setMarkedForReview(new Set(JSON.parse(savedReview)));
        } else if (data.attempt.marked_for_review) {
          setMarkedForReview(new Set(data.attempt.marked_for_review));
        }
      } catch (e) {
        if (data.attempt.marked_for_review) {
          setMarkedForReview(new Set(data.attempt.marked_for_review));
        }
      }
      
    } catch (err) {
      console.error('Start exam error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Auto-save answers
  useEffect(() => {
    if (!attempt || submitted) return;
    
    autoSaveRef.current = setInterval(() => {
      saveAnswers();
    }, 30000); // Every 30 seconds
    
    return () => {
      if (autoSaveRef.current) {
        clearInterval(autoSaveRef.current);
      }
    };
  }, [attempt, answers, submitted]);

  const saveAnswers = async () => {
    if (!attempt || submitted) return;
    
    try {
      await fetch(`${API_URL}/api/exams/public/attempt/${attemptId}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answers,
          marked_for_review: Array.from(markedForReview)
        })
      });
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const toggleMarkForReview = (questionId) => {
    setMarkedForReview(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleTimeUp = useCallback(() => {
    if (submittingRef.current) return;
    toast.error('Temps écoulé ! L\'examen va être soumis automatiquement.');
    handleSubmitFinal(true);
  }, [attemptId]);

  const handleSubmitFinal = async (forced = false) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    
    const currentAnswers = answersRef.current;
    
    try {
      // Save final answers to server first
      try {
        await fetch(`${API_URL}/api/exams/public/attempt/${attemptId}/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            answers: currentAnswers,
            marked_for_review: Array.from(markedForReview)
          })
        });
      } catch (e) {
        console.error('Pre-submit save failed:', e);
      }
      
      const res = await fetch(`${API_URL}/api/exams/public/attempt/${attemptId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answers: currentAnswers,
          forced: forced
        })
      });
      
      if (res.ok) {
        setSubmitted(true);
        clearLocalStorage();
        toast.success('Examen soumis avec succès !');
        
        setTimeout(() => {
          navigate(`/exam/${slug}/results/${attemptId}`);
        }, 2000);
      } else {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Erreur lors de la soumission');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message);
      submittingRef.current = false;
    } finally {
      setSubmitting(false);
      setShowSubmitDialog(false);
    }
  };

  const currentQuestion = questions[currentIndex];
  const answeredCount = Object.keys(answers).filter(id => 
    answers[id] !== null && answers[id] !== '' && 
    (Array.isArray(answers[id]) ? answers[id].length > 0 : true)
  ).length;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="text-center text-white">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" />
          <p>Chargement de l'examen...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Erreur</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate(`/exam/${slug}/register`)}>
              Retour à l'inscription
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Submitted state
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Examen soumis !</h2>
            <p className="text-muted-foreground">
              Vos réponses ont été enregistrées. Redirection vers les résultats...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200">
      <PublicAntiCheat 
        attemptId={attemptId}
        enabled={attempt?.exam?.settings?.enable_proctoring}
        onViolation={setViolations}
      />
      
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/50 shadow-sm supports-[backdrop-filter]:bg-white/60">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-extrabold text-xl bg-clip-text text-transparent bg-gradient-to-r from-purple-700 to-indigo-700">
              {attempt?.exam?.title || 'Examen'}
            </h1>
            <div className="flex items-center gap-4 text-sm font-medium text-slate-500 mt-1">
              <span className="bg-slate-100 px-2 py-0.5 rounded text-slate-600">Question {currentIndex + 1} / {questions.length}</span>
              <span className="bg-slate-100 px-2 py-0.5 rounded text-slate-600">{answeredCount} / {questions.length} répondu</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={toggleFullscreen}
              className="rounded-full shadow-sm bg-white hover:bg-slate-50 border-slate-200 text-slate-600"
              title={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>

            {violations > 0 && (
              <Badge 
                variant="destructive" 
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold transition-all duration-300",
                  violations >= WARNING_THRESHOLD 
                    ? "bg-red-600 animate-[pulse_1s_ease-in-out_infinite] shadow-[0_0_15px_rgba(239,68,68,0.8)] border border-red-500 text-white" 
                    : "bg-red-100 text-red-700 border border-red-200 hover:bg-red-200"
                )}
              >
                <AlertTriangle className="h-4 w-4" />
                {violations} avertissement{violations > 1 ? 's' : ''}
              </Badge>
            )}
            
            {timeRemaining !== null && (
              <Timer 
                timeRemaining={timeRemaining} 
                onTimeUp={handleTimeUp}
              />
            )}
            
            <Button
              onClick={() => setShowSubmitDialog(true)}
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-lg shadow-emerald-500/20 rounded-xl px-5 h-11 font-semibold transition-all hover:scale-105"
            >
              <Send className="h-4 w-4 mr-2" />
              Terminer
            </Button>
          </div>
        </div>
        
        {/* Progress bar */}
        <Progress 
          value={(answeredCount / questions.length) * 100} 
          className="h-1.5 bg-slate-200/50" 
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 grid lg:grid-cols-4 gap-8">
        {/* Question Navigator */}
        <div className="lg:col-span-1 order-2 lg:order-1">
          <Card className="sticky top-[120px] border-0 shadow-xl shadow-slate-200/50 bg-white/90 backdrop-blur-sm rounded-2xl ring-1 ring-slate-900/5">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-sm font-bold text-slate-700 uppercase tracking-wider">Navigation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-2">
                {questions.map((q, idx) => {
                  const isAnswered = answers[q.id] !== undefined && answers[q.id] !== null && answers[q.id] !== '';
                  const isMarked = markedForReview.has(q.id);
                  const isCurrent = idx === currentIndex;
                  
                  return (
                    <button
                      key={q.id}
                      onClick={() => setCurrentIndex(idx)}
                      className={cn(
                        "w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold transition-all duration-200 relative",
                        isCurrent ? "ring-2 ring-offset-2 ring-indigo-500 scale-110 shadow-md z-10" : "hover:scale-105 hover:shadow-sm",
                        isAnswered ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
                        isMarked && "ring-2 ring-amber-400"
                      )}
                    >
                      {idx + 1}
                      {isMarked && (
                        <div className="absolute -top-1.5 -right-1.5 bg-white rounded-full p-0.5 shadow-sm">
                          <Flag className="h-3 w-3 text-amber-500 fill-amber-500" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              
              <div className="mt-6 pt-4 border-t border-slate-100 space-y-3 test-sm font-medium text-slate-600">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-lg bg-emerald-100 border border-emerald-200" />
                  <span>Répondu</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-lg bg-slate-100 border border-slate-200" />
                  <span>Non répondu</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-lg bg-slate-100 border border-slate-200 ring-2 ring-amber-400 ring-offset-1" />
                  <span>À revoir</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Question Content */}
        <div className="lg:col-span-3 order-1 lg:order-2">
          {currentQuestion && (
            <Card className="border-0 shadow-xl shadow-slate-200/50 bg-white/90 backdrop-blur-sm rounded-2xl overflow-hidden ring-1 ring-slate-900/5">
              <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-6 border-b border-slate-100 bg-slate-50/50 px-8 pt-8">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <Badge variant="outline" className="bg-white border-indigo-100 text-indigo-700 px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-lg">
                      {currentQuestion.type === 'single_choice' && 'QCM unique'}
                      {currentQuestion.type === 'multiple_choice' && 'QCM multiple'}
                      {currentQuestion.type === 'true_false' && 'Vrai/Faux'}
                      {currentQuestion.type === 'short_answer' && 'Réponse courte'}
                      {currentQuestion.type === 'long_answer' && 'Réponse longue'}
                    </Badge>
                    <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100 px-3 py-1 font-bold rounded-lg border-none shadow-sm">
                      {currentQuestion.points} pt(s)
                    </Badge>
                  </div>
                  <div className="prose prose-slate max-w-none text-lg text-slate-800 leading-relaxed font-medium">
                    <div dangerouslySetInnerHTML={{ __html: currentQuestion.question_text }} />
                  </div>
                </div>
                
                <Button
                  variant={markedForReview.has(currentQuestion.id) ? "default" : "outline"}
                  size="sm"
                  onClick={() => toggleMarkForReview(currentQuestion.id)}
                  className={cn(
                    "rounded-xl font-semibold transition-all duration-300 ml-4 h-10 px-4",
                    markedForReview.has(currentQuestion.id) 
                      ? "bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/20" 
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  )}
                >
                  <Flag className={cn("h-4 w-4 mr-2", markedForReview.has(currentQuestion.id) ? "fill-white" : "")} />
                  {markedForReview.has(currentQuestion.id) ? 'Marqué pour révision' : 'Marquer pour révision'}
                </Button>
              </CardHeader>
              
              <CardContent className="p-8 space-y-6">
                {/* Answer inputs based on question type */}
                {(currentQuestion.type === 'single_choice' || currentQuestion.type === 'true_false') && (
                  <RadioGroup
                    value={answers[currentQuestion.id] || ''}
                    onValueChange={(value) => handleAnswer(currentQuestion.id, value)}
                    className="grid gap-3"
                  >
                    {currentQuestion.choices?.map((choice) => {
                      const isSelected = answers[currentQuestion.id] === choice.id;
                      return (
                        <div 
                          key={choice.id} 
                          className={cn(
                            "flex items-center space-x-4 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer group",
                            isSelected 
                              ? "bg-indigo-50/50 border-indigo-500 ring-4 ring-indigo-500/10 shadow-sm" 
                              : "bg-white border-slate-200 hover:border-indigo-300 hover:bg-slate-50"
                          )}
                          onClick={() => handleAnswer(currentQuestion.id, choice.id)}
                        >
                          <RadioGroupItem value={choice.id} id={choice.id} className={cn(
                            "w-5 h-5 border-2", 
                            isSelected ? "border-indigo-600 text-indigo-600" : "border-slate-300 group-hover:border-indigo-400"
                          )} />
                          <Label 
                            htmlFor={choice.id} 
                            className={cn(
                              "flex-1 cursor-pointer text-base select-none",
                              isSelected ? "text-indigo-900 font-medium" : "text-slate-700"
                            )}
                          >
                            {choice.text}
                          </Label>
                        </div>
                      );
                    })}
                  </RadioGroup>
                )}
                
                {currentQuestion.type === 'multiple_choice' && (
                  <div className="space-y-3">
                    {currentQuestion.choices?.map((choice) => {
                      const currentAnswers = answers[currentQuestion.id] || [];
                      const isChecked = currentAnswers.includes(choice.id);
                      
                      return (
                        <div 
                          key={choice.id} 
                          className={cn(
                            "flex items-center space-x-4 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer group",
                            isChecked 
                              ? "bg-indigo-50/50 border-indigo-500 ring-4 ring-indigo-500/10 shadow-sm" 
                              : "bg-white border-slate-200 hover:border-indigo-300 hover:bg-slate-50"
                          )}
                          onClick={() => {
                            let newAnswers;
                            if (!isChecked) {
                              newAnswers = [...currentAnswers, choice.id];
                            } else {
                              newAnswers = currentAnswers.filter(id => id !== choice.id);
                            }
                            handleAnswer(currentQuestion.id, newAnswers);
                          }}
                        >
                          <Checkbox
                            id={choice.id}
                            checked={isChecked}
                            onCheckedChange={() => {}} 
                            className={cn(
                              "w-5 h-5 rounded border-2", 
                              isChecked ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-300 group-hover:border-indigo-400"
                            )}
                          />
                          <Label 
                            htmlFor={choice.id} 
                            className={cn(
                              "flex-1 cursor-pointer text-base select-none",
                              isChecked ? "text-indigo-900 font-medium" : "text-slate-700"
                            )}
                          >
                            {choice.text}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {currentQuestion.type === 'short_answer' && (
                  <Input
                    value={answers[currentQuestion.id] || ''}
                    onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                    placeholder="Tapez votre réponse ici..."
                    className="max-w-xl h-14 text-lg rounded-xl border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 bg-slate-50/50"
                  />
                )}
                
                {currentQuestion.type === 'long_answer' && (
                  <Textarea
                    value={answers[currentQuestion.id] || ''}
                    onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                    placeholder="Tapez votre réponse détaillée ici..."
                    rows={8}
                    className="text-base rounded-xl border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 bg-slate-50/50 resize-y p-4"
                  />
                )}
              </CardContent>
              
              {/* Navigation buttons */}
              <div className="flex justify-between p-8 bg-slate-50/50 border-t border-slate-100">
                <Button
                  variant="outline"
                  onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                  disabled={currentIndex === 0}
                  className="rounded-xl px-6 h-12 font-semibold text-slate-700 bg-white border-slate-200 hover:bg-slate-50 hover:text-indigo-700 transition-all hover:scale-105 shadow-sm"
                >
                  <ChevronLeft className="h-5 w-5 mr-2" />
                  Précédent
                </Button>
                
                <Button
                  onClick={() => setCurrentIndex(Math.min(questions.length - 1, currentIndex + 1))}
                  disabled={currentIndex === questions.length - 1}
                  className="rounded-xl px-6 h-12 font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition-all shadow-md shadow-indigo-600/20 hover:shadow-lg hover:shadow-indigo-600/30 hover:scale-105"
                >
                  Suivant
                  <ChevronRight className="h-5 w-5 ml-2" />
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Submit confirmation dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Terminer l'examen ?</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-4">
                <p>Êtes-vous sûr de vouloir soumettre vos réponses ?</p>
                
                <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span>Questions répondues</span>
                    <span className="font-medium">{answeredCount}/{questions.length}</span>
                  </div>
                  {markedForReview.size > 0 && (
                    <div className="flex justify-between text-amber-600">
                      <span>À revoir</span>
                      <span className="font-medium">{markedForReview.size}</span>
                    </div>
                  )}
                </div>
                
                {answeredCount < questions.length && (
                  <p className="text-amber-600 text-sm">
                    Attention: {questions.length - answeredCount} question(s) non répondue(s).
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Continuer l'examen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleSubmitFinal(false)}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Soumission...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Confirmer
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
